import axios, { AxiosError, AxiosResponse } from 'axios';

import { store } from '@/store';
import { StatusCodeConstant } from '@/constants/status-code-constant';
import { ErrorMessageConstant } from '@/constants/error-message-constant';

axios.defaults.withCredentials = false;
axios.defaults.baseURL = window.API_URL;
axios.defaults.headers.common['Accept'] = 'application/json';
axios.defaults.headers.common['Content-Type'] = 'application/json';

const axiosInterceptor: ((transform: (msg: string) => string) => (error: AxiosError) => Promise<never>)  = (transform: (msg: string) => string) =>  {
	// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
	return ((error: AxiosError) => {
		let errorMessage: string;

		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		if (error.response?.data?.problems?.length > 0) {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			const apiErrorMessage = error.response
				.data.problems[0].message.replace(/^Une erreur est survenue lors de l'appel *: *\{ *code: '\d+', *message *: *'(?<msg>.+)', *stack *: *'.+' *}$/, '$1');
			errorMessage = transform(apiErrorMessage);
		} else if (!error.response || error.response.status >= StatusCodeConstant.INTERNAL_SERVER_ERROR) {
			// If we don't have an error or if we have a 500 HTTP Code
			// Use the default message
			errorMessage = ErrorMessageConstant.DEFAULT_ERROR_MESSAGE;
		} else if (error.response.status === StatusCodeConstant.FORBIDDEN) {
			errorMessage = ErrorMessageConstant.FORBIDDEN_ERROR_MESSAGE;
		} else {
			// Get the error message from the response
			errorMessage = error.message;
		}

		if (errorMessage) {
			// Send notification error
			store.dispatch('notification/add', {
				type: 'error',
				message: errorMessage
			});
		}

		return Promise.reject(error);
	});
};

/**
 * Map les messages d'erreurs récupérés depuis la BFF
 * @param msg	le message d'erreur
 */
function mapDataErrors(msg: string): string {

	// Regex qui permet de récupérer le nom du champ ([\\w ]+ = tout char alphanumérique + espaces)
	// et la description (>[^,\\}]+ = tout char n'étant pas ',' ou '}') et (?:, )? potentiel ",'space'"
	const lineRgx = /(?<fieldName>[\w ]+)=(?<errDesc>[^,}]+)(?:, )?/g;

	// Regex qui inclue le map des erreurs
	const mapRgx = new RegExp('^Les données fournies dans la requête ne sont pas valides\\. *Veuillez corriger ces données et soumettre à nouveau votre demande ' +
		`\\(données invalides = \\{(?<map>(?:${lineRgx.source})+)\\}\\)\\.$`);

	const execMap = mapRgx.exec(msg);

	// Ne match pas au pattern
	if (!execMap?.groups?.map) {
		return msg;
	}

	// On ne récupère que le map
	const m = execMap.groups.map;

	// Init result
	interface ResMap {
		[key: string]: string | undefined;
	}
	const res: ResMap = {};

	// Loop sur les matchs de la RegExp d'une data de la map
	let match;
	while ((match = lineRgx.exec(m)) != null) {
		// On ajoute { fieldName : errDesc } dans le result
		if (match?.groups?.fieldName) {
			res[match.groups.fieldName] = match.groups.errDesc;
		}
	}

	return 'Erreur de données dans le formulaire sur les champs suivants :\n' +
		Object.entries(res).map((err) => {
			const [ fieldName, errDesc ] = err;
			return `    ${fieldName} : ${errDesc}`;
	}).join('\n');
}

// Response interceptor to handle errors globally
axios.interceptors.response.use(undefined, axiosInterceptor(mapDataErrors));

export { axios, axiosInterceptor, mapDataErrors };
export { AxiosResponse };
